// Near/far-leg confidence panel (Stage 2 robustness, made visible).
//
// A lateral clip sees one leg camera-near (reliable) and one occluded far leg
// (less reliable). This panel renders that split honestly: a side-profile figure
// with the near leg lit and the far leg dimmed, a visibility meter per side, and
// the same per-side confidence the findings use — so the clinician knows which
// side's numbers to read with more caution.

import type { LegProximity } from "../../gait/robustness";
import type { Finding, Confidence } from "../../gait/bands";
import type { Side } from "../../pose/landmarks";

interface Props {
  proximity: LegProximity;
  findings: Finding[];
}

const CONF_META: Record<Confidence, { label: string; cls: string }> = {
  high: { label: "High", cls: "ok" },
  medium: { label: "Medium", cls: "mid" },
  low: { label: "Low", cls: "low" },
};

function confFor(side: Side, findings: Finding[], visibility: number): Confidence {
  // Prefer the confidence the findings actually report for this side (keeps the
  // panel consistent with the chips); fall back to a visibility heuristic.
  const f = findings.find((x) => x.side === side);
  if (f) return f.confidence;
  if (visibility >= 0.7) return "high";
  if (visibility >= 0.55) return "medium";
  return "low";
}

/** Side-profile figure: near leg lit, far leg dimmed + dashed. */
function FigureSvg({ near }: { near: Side }) {
  // Geometry of a mid-stride side profile (walking to the right).
  const hipX = 60;
  const hipY = 92;
  const front = { knee: [78, 126], ankle: [92, 162] }; // leading leg
  const back = { knee: [44, 128], ankle: [30, 164] }; // trailing leg
  // Convention: the camera-near leg is drawn solid/lit; here we light the
  // leading leg as "near" if near==L else the trailing leg, purely for an
  // intuitive at-a-glance read (the label states the actual side).
  const nearLeg = near === "L" ? front : back;
  const farLeg = near === "L" ? back : front;

  const leg = (
    pts: { knee: number[]; ankle: number[] },
    opts: { lit: boolean },
  ) => (
    <g
      stroke={opts.lit ? "url(#legNear)" : "rgba(245,245,247,0.22)"}
      strokeWidth={opts.lit ? 7 : 5}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
      strokeDasharray={opts.lit ? undefined : "2 7"}
      filter={opts.lit ? "url(#legGlow)" : undefined}
    >
      <polyline points={`${hipX},${hipY} ${pts.knee[0]},${pts.knee[1]} ${pts.ankle[0]},${pts.ankle[1]}`} />
      {/* foot */}
      <line
        x1={pts.ankle[0]}
        y1={pts.ankle[1]}
        x2={pts.ankle[0] + 16}
        y2={pts.ankle[1]}
      />
    </g>
  );

  return (
    <svg viewBox="0 0 120 180" width="108" height="160" aria-hidden="true">
      <defs>
        <linearGradient id="legNear" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#7cc4ff" />
          <stop offset="100%" stopColor="#5eead4" />
        </linearGradient>
        <filter id="legGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* far leg first (behind) */}
      {leg(farLeg, { lit: false })}
      {/* torso + head */}
      <g stroke="rgba(245,245,247,0.5)" strokeWidth={7} strokeLinecap="round" fill="none">
        <line x1={hipX} y1={hipY} x2={66} y2={44} />
      </g>
      <circle cx={68} cy={32} r={11} fill="rgba(245,245,247,0.55)" />
      {/* near leg on top */}
      {leg(nearLeg, { lit: true })}
    </svg>
  );
}

function SideMeter({
  role,
  side,
  visibility,
  confidence,
}: {
  role: "near" | "far";
  side: Side;
  visibility: number;
  confidence: Confidence;
}) {
  const pct = Math.round(visibility * 100);
  const meta = CONF_META[confidence];
  return (
    <div className={`legrow ${role}`}>
      <div className="legrow-head">
        <span className="legrow-role">{role === "near" ? "Camera-side" : "Far (occluded)"}</span>
        <span className="legrow-side">{side}</span>
      </div>
      <div className="legmeter">
        <div className={`legmeter-fill ${role}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="legrow-foot">
        <span className="muted">{pct}% visible</span>
        <span className={`conf-pill ${meta.cls}`}>{meta.label} confidence</span>
      </div>
    </div>
  );
}

export function LegConfidence({ proximity, findings }: Props) {
  const { near, far, nearVisibility, farVisibility, separation } = proximity;
  const nearConf = confFor(near, findings, nearVisibility);
  const farConf = confFor(far, findings, farVisibility);
  const clearSplit = separation >= 0.12;

  return (
    <div className="card legconf">
      <strong>Camera perspective</strong>
      <div className="legconf-body">
        <div className="legconf-figure">
          <FigureSvg near={near} />
        </div>
        <div className="legconf-meters">
          <SideMeter role="near" side={near} visibility={nearVisibility} confidence={nearConf} />
          <SideMeter role="far" side={far} visibility={farVisibility} confidence={farConf} />
        </div>
      </div>
      <p className="muted legconf-note">
        {clearSplit ? (
          <>
            The <b style={{ color: "var(--text)" }}>{near}</b> leg is camera-side and
            most reliable. The <b style={{ color: "var(--text)" }}>{far}</b> leg is
            partially occluded by the body, so its metrics are read with{" "}
            {CONF_META[farConf].label.toLowerCase()} confidence.
          </>
        ) : (
          <>Both legs are similarly visible — neither side is favoured for this clip.</>
        )}
      </p>
    </div>
  );
}
